import React, { PropsWithChildren, useContext, useEffect } from 'react'

import { withOptimizely, WithOptimizelyProps } from '@optimizely/react-sdk'
import { backdropContext, PageWrapper } from '@vividseats/vivid-ui-kit'

import AccesibilityNavigation from '@/components/shared/accessibility-navigation'
import Meta from '@/components/shared/seo-meta'
import { HeaderStyleProvider } from '@/context/header'
import { useForceUpdate } from '@/hooks/use-force-update'
import StickySearchBarHeader from '@/optimizely/features/search-bar-feature/components/sticky-search-bar-header'
import { useSearchBarFeature } from '@/optimizely/features/search-bar-feature/hooks/use-search-bar-feature'
import type { Metadata } from '@/types/app-types'

import Footer from './components/footer'
import Header from './components/header'
import styles from './styles.module.scss'

type Props = {
    metadata?: Metadata
    shouldRenderHeader?: boolean
    shouldRenderPageWrapper?: boolean
    shouldRenderFooter?: boolean
    isDisclaimerSticky?: boolean
    headerStyleProvider?: boolean
    white?: boolean
    mobileOnlyPaidSearchDisclaimer?: boolean
} & WithOptimizelyProps

const MainLayout: React.FC<PropsWithChildren<Props>> = ({
    children,
    metadata,
    shouldRenderHeader = true,
    shouldRenderPageWrapper = true,
    shouldRenderFooter = true,
    headerStyleProvider = false,
    white = false,
    mobileOnlyPaidSearchDisclaimer = false,
    optimizely,
}) => {
    const [isSearchBarFeatureEnabled] = useSearchBarFeature(optimizely)

    // This is to force page to re-render after searchbar click so that it realizes the change in isSearchbarFeatureEnabled
    const {
        state: { toggled: isBackdropOpen },
    } = useContext(backdropContext)
    const forceUpdate = useForceUpdate()
    useEffect(() => {
        if (isBackdropOpen) forceUpdate()
    }, [isBackdropOpen])

    const content = (
        <>
            {shouldRenderHeader &&
                (headerStyleProvider ? (
                    <HeaderStyleProvider initialProps={{ listenForScroll: true }}>
                        {isSearchBarFeatureEnabled ? (
                            <StickySearchBarHeader mobileOnlyPaidSearchDisclaimer={mobileOnlyPaidSearchDisclaimer} />
                        ) : (
                            <Header mobileOnlyPaidSearchDisclaimer={mobileOnlyPaidSearchDisclaimer} />
                        )}
                    </HeaderStyleProvider>
                ) : isSearchBarFeatureEnabled ? (
                    <StickySearchBarHeader mobileOnlyPaidSearchDisclaimer={mobileOnlyPaidSearchDisclaimer} />
                ) : (
                    <Header mobileOnlyPaidSearchDisclaimer={mobileOnlyPaidSearchDisclaimer} />
                ))}
            {children}
            {shouldRenderFooter && <Footer />}
        </>
    )

    const pageWrapperWithContent = shouldRenderPageWrapper ? (
        <PageWrapper white={white} className={styles.fullHeight}>
            {content}
        </PageWrapper>
    ) : (
        content
    )

    return (
        <React.Fragment>
            <Meta {...metadata} />
            <AccesibilityNavigation />
            {pageWrapperWithContent}
        </React.Fragment>
    )
}

export default withOptimizely(MainLayout)
